/**
 * A seção *Esperas* do detalhe do Caso (`TP-048`, `T6`).
 *
 * A espera da operadora é DERIVADA do estado — não tem botão, só aparece na lista quando aberta ou fechada. A da
 * família é um ATO: abrir e fechar, cada um com confirmação visual do próprio botão (desabilitado quando não vale).
 */

import Button from '@mui/material/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../shared/api/erros';
import { formatarDataHora } from '../../shared/dominio/datas';
import { abrirEsperaDaFamilia, fecharEspera } from './api/escritas';
import { chavesDeCasos } from './api/casos';
import {
  ROTULOS_DO_TIPO_DE_ESPERA,
  formatarDuracaoEmHorasEMinutos,
  type CasoDetalhado,
} from './api/tipos';

/** Mensagem de cada desfecho, por `codigo`/`status` — mesma disciplina de `AcoesDoCaso.tsx`. */
function mensagemDeErroDaEspera(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    if (causa.codigo === 'WAIT_ALREADY_OPEN') {
      return 'Já existe uma espera da família aberta para este caso.';
    }
    if (causa.codigo === 'WAIT_NOT_OPEN') {
      return 'Esta espera já estava fechada, ou é de outro caso.';
    }
    if (causa.status === 404) return 'O caso não foi encontrado para o seu núcleo.';
    if (causa.status === 422) return 'Caso encerrado: não aceita mais esperas.';
    if (causa.status === 400) return 'A API recusou esta espera.';
  }
  return 'Não foi possível concluir a ação. Tente novamente.';
}

export function EsperasDoCaso({
  caso,
  aoAtualizar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();

  const invalidarLeiturasRelacionadas = (): void => {
    void clienteDeConsultas.invalidateQueries({ queryKey: [...chavesDeCasos.raiz, 'lista'] });
    void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.linhaDoTempo(caso.id) });
  };

  const abrir = useMutation({
    mutationFn: () => abrirEsperaDaFamilia(caso.id),
    onSuccess: (atualizado) => {
      aoAtualizar(atualizado);
      invalidarLeiturasRelacionadas();
    },
  });

  const fechar = useMutation({
    mutationFn: (esperaId: string) => fecharEspera(caso.id, esperaId),
    onSuccess: (atualizado) => {
      aoAtualizar(atualizado);
      invalidarLeiturasRelacionadas();
    },
  });

  const encerrado = caso.estado === 'Closed';
  const familiaJaAberta = (caso.espera?.abertas ?? []).includes('Family');
  const erro = abrir.error ?? fechar.error;

  return (
    <div className="space-y-3">
      <dl className="anery-ficha-caso">
        <dt>Tempo de espera</dt>
        <dd>
          {caso.espera === null ? (
            'Nenhuma espera registrada.'
          ) : (
            <>
              tempo total do caso {formatarDuracaoEmHorasEMinutos(caso.espera.totalSegundos)} · parado em
              terceiros {formatarDuracaoEmHorasEMinutos(caso.espera.deTerceiroSegundos)} (descontado do prazo) ·
              nosso {formatarDuracaoEmHorasEMinutos(caso.espera.nossoSegundos)}
            </>
          )}
        </dd>
      </dl>

      {caso.esperas.length === 0 ? (
        <p className="text-[0.8125rem] text-[var(--tinta-2)]">Nenhuma espera por terceiro registrada para este caso.</p>
      ) : (
        <ul className="list-none pl-0">
          {caso.esperas.map((espera) => {
            const idDaEspera = espera.id;
            return (
              <li key={idDaEspera ?? `${espera.tipo}-${espera.inicio.toISOString()}`} className="anery-item">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{ROTULOS_DO_TIPO_DE_ESPERA[espera.tipo]}</span>
                  <span className="block text-xs text-[var(--tinta-2)]">
                    {espera.origem === 'State'
                      ? 'derivada do estado'
                      : `aberta por ${espera.abertaPorNome ?? 'pessoa fora do cadastro'}`}
                    {' · '}
                    {formatarDataHora(espera.inicio)}
                    {' até '}
                    {espera.fim === null ? 'aberta' : formatarDataHora(espera.fim)}
                  </span>
                </span>
                {/* A operadora (`State`) não tem botão: é derivada, ninguém fecha à mão. */}
                {espera.origem === 'Manual' && espera.fim === null && idDaEspera !== null ? (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={fechar.isPending}
                    onClick={() => {
                      abrir.reset();
                      fechar.mutate(idDaEspera);
                    }}
                  >
                    Família respondeu
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Button
        variant="outlined"
        disabled={abrir.isPending || familiaJaAberta || encerrado}
        onClick={() => {
          fechar.reset();
          abrir.mutate();
        }}
      >
        Aguardando a família
      </Button>

      {erro ? (
        <p role="alert" aria-live="polite" className="text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDaEspera(erro)}
        </p>
      ) : null}
    </div>
  );
}
